import { useCallback, useRef, useEffect } from 'react';
import authService from '../../services/auth.service';
import {
  UI_DELAY_BACKGROUND,
  REQUEST_TIMEOUT_SESSION_VERIFICATION,
} from '../../config/constants';
import { debugLog, debugWarn } from '../../utils/debugLog';
import { extractAuthDataFromToken } from '../AuthContextUtils';

/**
 * useSessionVerification Hook
 * Manages background session verification logic
 */
export const useSessionVerification = ({
  setUser,
  setTenant,
  setGroups,
  setIsAuthenticated,
}) => {
  // Refs to prevent duplicate verification calls
  const verificationInProgressRef = useRef(false);
  const verificationTimeoutRef = useRef(null);

  /**
   * Handle session verification result
   * Updates user/tenant state based on verification result
   */
  const handleSessionVerification = useCallback(
    async (sessionResult, cachedTenant) => {
      if (!sessionResult.isValid) {
        // Session is invalid - check if it's a real auth error or network error
        if (sessionResult.error === 'auth_error') {
          // Real authentication failure (401, invalid token, etc.)
          debugWarn(
            '⚠️ Session verification failed - authentication error, clearing auth'
          );
          debugLog(
            '🔍 Before clearing - token exists:',
            !!authService.getToken()
          );
          const tenantToPreserve =
            sessionResult.tenant || authService.getTenant();
          authService.clearToken();
          authService.clearUser();
          debugLog(
            '🔍 After clearing - token exists:',
            !!authService.getToken()
          );
          setUser(null);
          setGroups([]); // Clear groups on auth error
          setIsAuthenticated(false);
          if (tenantToPreserve) {
            setTenant(tenantToPreserve);
          } else {
            setTenant(null);
          }
        } else {
          // Network error or timeout - keep user logged in with cached data
          debugWarn(
            '⚠️ Session verification failed - network error, keeping auth with cached data'
          );
          // Don't clear authentication - keep user logged in
        }
      } else {
        // Update with fresh data from server if available
        if (sessionResult.user) {
          // Extract groups from JWT token if not in user object
          const token = authService.getToken();
          const authData = extractAuthDataFromToken(token);

          // Preserve picture from cached user if session result doesn't include it
          const currentCachedUser = authService.getUser();

          const userWithGroups = {
            ...sessionResult.user,
            groups: sessionResult.user.groups || authData.groups || [],
            role: sessionResult.user.role || authData.role || 'user',
            picture:
              sessionResult.user.picture ||
              (currentCachedUser && currentCachedUser.picture) ||
              null,
          };

          setUser(userWithGroups);
          setGroups(userWithGroups.groups || []);
          authService.setUser(userWithGroups);
        } else {
          // If no user in session result, extract from token
          const token = authService.getToken();
          if (token) {
            const authData = extractAuthDataFromToken(token);
            setGroups(authData.groups || []);
          }
        }

        if (sessionResult.tenant) {
          // Preserve adapters from cached tenant if session result doesn't include it
          const currentCachedTenant = authService.getTenant();
          const tenantToSet = {
            ...sessionResult.tenant,
            adapters:
              sessionResult.tenant.adapters ||
              (currentCachedTenant && currentCachedTenant.adapters) ||
              {},
          };
          setTenant(tenantToSet);
          authService.setTenant(tenantToSet);
        } else {
          // Session result doesn't include tenant, keep cached tenant
          const currentCachedTenant = authService.getTenant();
          if (currentCachedTenant) {
            debugLog(
              '✅ Session verification - no tenant in result, preserving cached tenant'
            );
          }
        }
      }
    },
    [setUser, setTenant, setGroups, setIsAuthenticated]
  );

  /**
   * Handle session verification errors
   * Distinguishes between timeout, auth errors, and network errors
   */
  const handleSessionVerificationError = useCallback(
    error => {
      // Handle different types of errors
      const isTimeout =
        error.message && error.message.includes('SESSION_VERIFICATION_TIMEOUT');
      const isAuthError =
        error.message &&
        (error.message.includes('401') ||
          error.message.includes('Unauthorized') ||
          error.message.includes('Invalid token'));

      if (isTimeout) {
        // Timeout is not a critical error - keep user logged in with cached data
        debugLog(
          'ℹ️ Session verification timeout - keeping user logged in with cached data'
        );
        // Don't clear authentication - this is just a timeout, not an auth failure
      } else if (isAuthError) {
        // Real authentication error - clear auth
        debugWarn(
          '⚠️ Background session verification failed - authentication error, clearing auth'
        );
        authService.clearToken();
        authService.clearUser();
        setUser(null);
        setIsAuthenticated(false);
      } else {
        // Other errors (network, etc.) - keep user logged in
        debugWarn(
          '⚠️ Background session verification failed (network error) - keeping user logged in:',
          error.message
        );
        // Don't clear authentication - keep user logged in with cached data
      }
    },
    [setUser, setIsAuthenticated]
  );

  /**
   * Perform background session verification
   * Runs asynchronously after optimistic authentication
   * Prevents duplicate calls if already in progress
   */
  const performBackgroundSessionVerification = useCallback(
    cachedTenant => {
      // Prevent duplicate verification calls
      if (verificationInProgressRef.current) {
        debugLog(
          '⏭️ Session verification already in progress, skipping duplicate call'
        );
        return;
      }

      // Clear any existing timeout
      if (verificationTimeoutRef.current) {
        clearTimeout(verificationTimeoutRef.current);
        verificationTimeoutRef.current = null;
      }

      // Mark verification as in progress
      verificationInProgressRef.current = true;

      verificationTimeoutRef.current = setTimeout(async () => {
        try {
          const sessionResult = await Promise.race([
            authService.verifySession(),
            new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error('SESSION_VERIFICATION_TIMEOUT')),
                REQUEST_TIMEOUT_SESSION_VERIFICATION
              )
            ),
          ]);

          await handleSessionVerification(sessionResult, cachedTenant);
        } catch (error) {
          handleSessionVerificationError(error);
        } finally {
          // Reset verification flag after completion
          verificationInProgressRef.current = false;
          verificationTimeoutRef.current = null;
        }
      }, UI_DELAY_BACKGROUND); // Small delay to not block initial render
    },
    [handleSessionVerification, handleSessionVerificationError]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (verificationTimeoutRef.current) {
        clearTimeout(verificationTimeoutRef.current);
        verificationTimeoutRef.current = null;
      }
    };
  }, []);

  return {
    performBackgroundSessionVerification,
  };
};
